import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, AudioLines, Ban, ChevronLeft, Compass, Image as ImageIcon, Import, Link2, Lock, Plus, ScrollText, Settings, ShieldCheck, Smile, Sticker, Tag, Users, Webhook, Zap } from 'lucide-react'
import { useAppStore, type GuildSettingsSection } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { displayName, useSocialStore } from '@/store/social'
import { useSelfStatus } from '@/store/presence'
import { isOnline } from '@/lib/presence'
import { guildApi } from '@/lib/guildApi'
import { describeError, publicUser } from '@/lib/auth'
import { ALL, P, PERMISSION_GROUPS, ROLE_LOCK_EDIT, assignLockHint, basePermissions, bits, has, roleColor, roleLock, topRole, type PermissionKey, type RoleLock } from '@/lib/permissions'
import { formatDateTime } from '@/lib/format'
import { ANIMATED_BANNER_TIER, BANNER_TIER, guildColor, guildInitials, inviteLink, memberName, plural } from '@/lib/guildUi'
import { avatarUrl, fileDataUrl, fitAvatar, fitBanner, guildBannerUrl, guildIconUrl, memberAvatarUrl } from '@/lib/avatar'
import { isAnimatedFile } from '@/lib/animatedImage'
import { ChannelType, type AuditLogEntry, type Guild, type Member, type Role, type User } from '@/types/api'
import { cx } from '@/lib/cx'
import { Avatar } from '../Avatar'
import { ConfirmModal } from './ConfirmModal'
import { AutomodSection } from './AutomodSection'
import { EmojisSection } from './EmojisSection'
import { StickersSection } from './StickersSection'
import { SoundboardSection } from './SoundboardSection'
import { BoostSection } from './BoostSection'
import { DiscoverySection } from './DiscoverySection'
import { WebhooksSection } from './WebhooksSection'
import { ImportSection } from './ImportSection'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import { SettingsFrame, type FrameGroup, type FrameItem } from '../settings/SettingsFrame'
import styles from './GuildSettingsLayer.module.css'
import { colorNumber, hexColor } from '@/lib/decor'
import { ServerTag } from '../ServerTag'
import { ChannelNameInput } from './ChannelNameInput'
import { Select, type SelectOption } from '../Select'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`
const hex = (c: number) => `#${c.toString(16).padStart(6, '0')}`
const SWATCHES = ['#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#e91e63', '#f1c40f', '#e67e22', '#e74c3c', '#95a5a6', '#607d8b']

/** Настройки сервера: обзор, роли, участники, приглашения, баны — по правам участника. */
export function GuildSettingsLayer({ guildId, section }: { guildId: string; section: GuildSettingsSection }) {
  return <GuildSettingsInner guildId={guildId} section={section} />
}

const ICON = 20

function GuildSettingsInner({ guildId, section }: { guildId: string; section: GuildSettingsSection }) {
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const member = useGuildStore((s) => (me ? s.members[guildId]?.[me.id] : undefined))
  const open = useAppStore((s) => s.openGuildSettings)
  const close = useAppStore((s) => s.closeGuildSettings)

  if (!guild || !me) return null
  const self: Member = member ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }
  const perms = basePermissions(guild, self)
  const owner = guild.owner_id === me.id
  // раздел виден только тому, у кого есть на него права; пустые группы не показываем
  const when = (show: boolean, item: FrameItem): FrameItem[] => (show ? [item] : [])
  const groups: FrameGroup[] = [
    {
      key: 'server',
      items: [
        { id: 'overview', label: 'Обзор', icon: <Settings size={ICON} /> },
        ...when(has(perms, P.MANAGE_ROLES), { id: 'roles', label: 'Роли', icon: <Tag size={ICON} /> }),
        { id: 'emojis', label: 'Эмодзи', icon: <Smile size={ICON} /> },
        { id: 'stickers', label: 'Стикеры', icon: <Sticker size={ICON} /> },
        { id: 'soundboard', label: 'Звуковая панель', icon: <AudioLines size={ICON} /> },
      ],
    },
    {
      key: 'people',
      title: 'Управление участниками',
      items: [
        { id: 'members', label: 'Участники', icon: <Users size={ICON} /> },
        ...when(has(perms, P.MANAGE_GUILD), { id: 'invites', label: 'Приглашения', icon: <Link2 size={ICON} /> }),
      ],
    },
    {
      key: 'moderation',
      title: 'Модерация',
      items: [
        ...when(has(perms, P.MANAGE_GUILD), { id: 'automod', label: 'Автомод', icon: <ShieldCheck size={ICON} /> }),
        ...when(has(perms, P.BAN_MEMBERS), { id: 'bans', label: 'Баны', icon: <Ban size={ICON} /> }),
        ...when(has(perms, P.VIEW_AUDIT_LOG), { id: 'audit', label: 'Журнал аудита', icon: <ScrollText size={ICON} /> }),
      ],
    },
    {
      key: 'integrations',
      title: 'Интеграции',
      items: [
        ...when(has(perms, P.MANAGE_WEBHOOKS), { id: 'webhooks', label: 'Вебхуки', icon: <Webhook size={ICON} /> }),
        ...when(owner, { id: 'import', label: 'Импорт из Discord', icon: <Import size={ICON} /> }),
      ],
    },
    {
      key: 'growth',
      items: [
        { id: 'boost', label: 'Буст сервера', icon: <Zap size={ICON} /> },
        ...when(owner, { id: 'discovery', label: 'Каталог', icon: <Compass size={ICON} /> }),
      ],
    },
  ].filter((g) => g.items.length > 0)
  const active = groups.some((g) => g.items.some((i) => i.id === section)) ? section : 'overview'
  const icon = guildIconUrl(guild.id, guild.icon)

  return (
    <SettingsFrame
      label={`Настройки сервера ${guild.name}`}
      listTitle={guild.name}
      closeLabel="Закрыть настройки сервера"
      head={{
        node: (
          <>
            <span className={layer.guildIcon} style={icon ? undefined : { background: guildColor(guild.id) }} aria-hidden="true">
              {icon ? <img src={icon} alt="" /> : guildInitials(guild.name)}
            </span>
            <span className={layer.navHeadText}>
              <span className={layer.navHeadName}>{guild.name}</span>
              <span className={layer.navHeadSub}><span>Настройки сервера</span></span>
            </span>
          </>
        ),
      }}
      groups={groups}
      active={active}
      onSelect={(id) => open(guildId, id as GuildSettingsSection)}
      onClose={close}
      // из меню сервера на телефоне — список разделов; ссылкой на раздел («Как поднять уровень») — сразу он
      startWithList={section === 'overview'}
    >
      {active === 'overview' ? <Overview guild={guild} self={self} perms={perms} /> : null}
      {active === 'roles' ? <Roles guild={guild} self={self} perms={perms} /> : null}
      {active === 'members' ? <Members guild={guild} self={self} perms={perms} /> : null}
      {active === 'invites' ? <Invites guild={guild} /> : null}
      {active === 'bans' ? <Bans guild={guild} /> : null}
      {active === 'automod' ? <AutomodSection guild={guild} /> : null}
      {active === 'emojis' ? <EmojisSection guild={guild} canManage={has(perms, P.MANAGE_GUILD_EXPRESSIONS)} /> : null}
      {active === 'stickers' ? <StickersSection guild={guild} canManage={has(perms, P.MANAGE_GUILD_EXPRESSIONS)} /> : null}
      {active === 'soundboard' ? <SoundboardSection guild={guild} canManage={has(perms, P.MANAGE_GUILD_EXPRESSIONS)} canCreate={has(perms, P.CREATE_EXPRESSIONS)} /> : null}
      {active === 'boost' ? <BoostSection guildId={guildId} /> : null}
      {active === 'discovery' ? <DiscoverySection guildId={guildId} /> : null}
      {active === 'webhooks' ? <WebhooksSection guild={guild} /> : null}
      {active === 'import' ? <ImportSection guild={guild} /> : null}
      {active === 'audit' ? <AuditLogSection guild={guild} /> : null}
    </SettingsFrame>
  )
}

// --- Обзор -------------------------------------------------------------------------

/** Уровни проверки: подпись в поле, условие — второй строкой в списке. */
const VERIFICATION: SelectOption<number>[] = [
  { value: 0, label: 'Нет', description: 'Писать могут все' },
  { value: 1, label: 'Низкий', description: 'Аккаунт старше 24 часов' },
  { value: 2, label: 'Средний', description: 'Аккаунт старше 24 часов и 5 минут на сервере' },
  { value: 3, label: 'Высокий', description: 'Аккаунт старше 24 часов и 10 минут на сервере' },
]

/** Цвет тега сервера по умолчанию — фирменный синий. */
const TAG_COLOR = 0x5865f2

function Overview({ guild, self, perms }: { guild: Guild; self: Member; perms: bigint }) {
  const updateGuild = useGuildStore((s) => s.updateGuild)
  const deleteGuild = useGuildStore((s) => s.deleteGuild)
  const leaveGuild = useGuildStore((s) => s.leaveGuild)
  const channels = useGuildStore((s) => s.channels[guild.id])
  const setView = useAppStore((s) => s.setView)
  const close = useAppStore((s) => s.closeGuildSettings)
  const openGuildSettings = useAppStore((s) => s.openGuildSettings)
  const [name, setName] = useState(guild.name)
  const [description, setDescription] = useState(guild.description ?? '')
  const [system, setSystem] = useState(guild.system_channel_id ?? '')
  const [rules, setRules] = useState(guild.rules_channel_id ?? '')
  const [afkEnabled, setAFKEnabled] = useState(Boolean(guild.afk_channel_id))
  const [afkChannel, setAFKChannel] = useState(guild.afk_channel_id ?? '')
  const [afkTimeout, setAFKTimeout] = useState(guild.afk_timeout || 300)
  const [afkError, setAFKError] = useState<string | null>(null)
  const [verification, setVerification] = useState(guild.verification_level ?? 0)
  const [tag, setTag] = useState(guild.tag ?? '')
  const [tagColor, setTagColor] = useState(guild.tag_color ?? TAG_COLOR)
  const [progressBar, setProgressBar] = useState(Boolean(guild.premium_progress_bar_enabled))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bannerError, setBannerError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [confirm, setConfirm] = useState<'delete' | 'leave' | null>(null)
  // системное поле выбора файла спрятано: вместо «Выберите файл / Файл не выбран» — наша кнопка
  const iconFile = useRef<HTMLInputElement>(null)
  const bannerFile = useRef<HTMLInputElement>(null)
  const owner = guild.owner_id === self.user.id
  const canEdit = has(perms, P.MANAGE_GUILD)
  const textChannels = Object.values(channels ?? {}).filter((c) => c.type === ChannelType.GuildText)
  const channelOptions = [{ value: '', label: 'Нет' }, ...textChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))]

  const afkOptions = [{ value: '', label: 'Выберите канал' }, ...Object.values(channels ?? {}).filter(c => c.type === ChannelType.GuildVoice).map(c => ({ value: c.id, label: c.name ?? 'Голосовой канал' }))]

  // иконка уходит data URI — бэкенд кладёт её в хранилище и возвращает хэш
  const setIcon = async (icon: string | null) => {
    setBusy(true)
    setError(null)
    try {
      await updateGuild(guild.id, { icon })
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.icon ?? d.message)
    } finally {
      setBusy(false)
    }
  }
  const pickIcon = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      await setIcon(await fitAvatar(file, 256))
    } catch {
      setError('Не удалось прочитать картинку')
    }
  }

  // баннер: картинка — кадр 16:9 1280×720, GIF с 3-го уровня — как есть, чтобы не потерять движение
  const tier = guild.premium_tier
  const bannerUrl = guildBannerUrl(guild.id, guild.banner)
  const setBanner = async (banner: string | null) => {
    setBusy(true)
    setBannerError(null)
    try {
      await updateGuild(guild.id, { banner })
    } catch (err) {
      const d = describeError(err)
      setBannerError(d.fields.banner ?? d.message)
    } finally {
      setBusy(false)
    }
  }
  const pickBanner = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      // анимацию (GIF, WebP, APNG) отправляем как есть: пережатая в JPEG, она осталась бы первым кадром
      const animated = tier >= ANIMATED_BANNER_TIER && (await isAnimatedFile(file))
      if (animated && file.size > 10 << 20) {
        setBannerError('Анимация больше 10 МБ. Уменьшите её и попробуйте снова')
        return
      }
      await setBanner(animated ? await fileDataUrl(file) : await fitBanner(file, 1280, 720))
    } catch {
      setBannerError('Не удалось прочитать картинку')
    }
  }
  const bannerHint = tier >= ANIMATED_BANNER_TIER
    ? 'Картинка, GIF или анимированный WebP 16:9 от 960×540, до 10 МБ. Стоит над списком каналов'
    : tier >= BANNER_TIER
      ? 'Картинка 16:9 от 960×540, стоит над списком каналов. GIF — с 3-го уровня'
      : guild.banner ? 'Скрыт, пока у сервера нет 2-го уровня' : 'Открывается на 2-м уровне сервера'

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaved(false)
    setAFKError(null)
    if (afkEnabled && !afkChannel) { setAFKError('Выберите голосовой канал для AFK'); document.getElementById('gs-afk')?.focus(); return }
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await updateGuild(guild.id, { name: name.trim(), description: description.trim() || null, system_channel_id: system || null, rules_channel_id: rules || null, afk_channel_id: afkEnabled ? afkChannel : null, afk_timeout: afkTimeout, verification_level: verification, tag: tag.trim() || null, tag_color: tagColor, premium_progress_bar_enabled: progressBar })
      setSaved(true)
    } catch (err) {
      const d = describeError(err)
      if (d.fields.afk_channel_id) setAFKError(d.fields.afk_channel_id)
      else setError(d.fields.name ?? d.fields.tag ?? d.fields.afk_timeout ?? d.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <h1 className={layer.sectionTitle}>Обзор сервера</h1>
      <form className={auth.form} onSubmit={save} noValidate>
        <Field label="Название сервера" htmlFor="gs-name" error={error ?? undefined}>
          <input id="gs-name" className={auth.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} disabled={!canEdit} />
        </Field>
        <Field label="Иконка сервера" htmlFor="gs-icon" hint="PNG, JPEG, GIF или WebP до 2 МБ; показывается на панели слева и в каталоге">
          <div className={styles.iconRow}>
            <span className={styles.iconPreview} style={guildIconUrl(guild.id, guild.icon) ? undefined : { background: guildColor(guild.id) }}>
              {guildIconUrl(guild.id, guild.icon) ? <img src={guildIconUrl(guild.id, guild.icon)} alt="" /> : guildInitials(guild.name)}
            </span>
            <input ref={iconFile} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => void pickIcon(e)} />
            <button id="gs-icon" type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={!canEdit || busy} onClick={() => iconFile.current?.click()}>
              {guild.icon ? 'Заменить' : 'Загрузить'}
            </button>
            {guild.icon && canEdit ? <button type="button" className={styles.linkButton} disabled={busy} onClick={() => void setIcon(null)}>Убрать</button> : null}
          </div>
        </Field>
        <Field label="Баннер сервера" htmlFor="gs-banner" error={bannerError ?? undefined} hint={bannerHint}>
          <div className={styles.bannerRow}>
            <span className={styles.bannerPreview}>{bannerUrl ? <img src={bannerUrl} alt="" /> : <ImageIcon size={28} aria-hidden="true" />}</span>
            <div className={styles.bannerActions}>
              {tier >= BANNER_TIER ? (
                <>
                  <input ref={bannerFile} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => void pickBanner(e)} />
                  <button id="gs-banner" type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={!canEdit || busy} onClick={() => bannerFile.current?.click()}>
                    {guild.banner ? 'Заменить' : 'Загрузить'}
                  </button>
                </>
              ) : (
                <button id="gs-banner" type="button" className={styles.linkButton} onClick={() => openGuildSettings(guild.id, 'boost')}>Как поднять уровень</button>
              )}
              {guild.banner && canEdit ? <button type="button" className={styles.linkButton} disabled={busy} onClick={() => void setBanner(null)}>Убрать</button> : null}
            </div>
          </div>
        </Field>
        <label className={styles.switch}>
          <input type="checkbox" checked={progressBar} onChange={(e) => setProgressBar(e.target.checked)} disabled={!canEdit} />
          <span>Показывать над каналами, сколько бустов до следующего уровня</span>
        </label>
        <Field label="Описание" htmlFor="gs-desc" hint="Видно в приглашении и в каталоге серверов">
          <textarea id="gs-desc" className={auth.input} rows={3} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} />
        </Field>
        <Field label="Канал правил" htmlFor="gs-rules" hint="Нужен для публикации сервера в каталоге">
          <Select id="gs-rules" value={rules} options={channelOptions} onChange={setRules} disabled={!canEdit} />
        </Field>
        <Field label="Канал системных сообщений" htmlFor="gs-system" hint="Сюда приходят сообщения о новых участниках">
          <Select id="gs-system" value={system} options={channelOptions} onChange={setSystem} disabled={!canEdit} />
        </Field>
        <h2 className={layer.subTitle}>AFK</h2>
        <label className={styles.switch}>
          <input type="checkbox" checked={afkEnabled} onChange={e => { setAFKEnabled(e.target.checked); setAFKError(null) }} disabled={!canEdit || busy} />
          <span>Переносить неактивных участников в AFK</span>
        </label>
        <Field label="Канал AFK" htmlFor="gs-afk" error={afkError ?? undefined} hint="В этом канале микрофон и звук отключены">
          <Select id="gs-afk" value={afkChannel} options={afkOptions} onChange={value => { setAFKChannel(value); setAFKError(null) }} disabled={!canEdit || busy || !afkEnabled} />
        </Field>
        <Field label="Время бездействия" htmlFor="gs-afk-timeout" hint="Разговор, камера и трансляция предотвращают перенос">
          <Select id="gs-afk-timeout" value={afkTimeout} options={[{ value: 60, label: '1 минута' }, { value: 300, label: '5 минут' }, { value: 900, label: '15 минут' }, { value: 1800, label: '30 минут' }, { value: 3600, label: '60 минут' }]} onChange={setAFKTimeout} disabled={!canEdit || busy || !afkEnabled} />
        </Field>
        <Field label="Тег сервера" htmlFor="gs-tag" hint="2–4 буквы или цифры. Участники смогут носить его рядом с ником: в чате, списке и профиле">
          <div className={styles.tagRow}>
            <input id="gs-tag" className={cx(auth.input, styles.tagInput)} value={tag} maxLength={4} placeholder="ВЕЧ" onChange={(e) => setTag(e.target.value)} disabled={!canEdit} />
            <input type="color" className={styles.tagColor} value={hexColor(tagColor)} onChange={(e) => setTagColor(colorNumber(e.target.value))} disabled={!canEdit} aria-label="Цвет тега" />
            {tag.trim() ? <ServerTag tag={tag.trim()} color={tagColor} /> : null}
          </div>
        </Field>
        <Field label="Уровень проверки" htmlFor="gs-verify" hint="Кому можно писать; участники с ролями, администраторы и владелец не проверяются">
          <Select id="gs-verify" value={verification} options={VERIFICATION} onChange={setVerification} disabled={!canEdit} />
        </Field>
        {canEdit ? (
          <div className={layer.buttonRow}>
            <button type="submit" className={layer.button} disabled={busy || name.trim().length < 2}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
            {saved ? <span className={styles.saved}>Сохранено</span> : null}
          </div>
        ) : null}
      </form>
      <div className={layer.divider} />
      <h2 className={layer.subTitle}>{owner ? 'Опасная зона' : 'Участие'}</h2>
      <p className={layer.text}>
        {owner ? 'Удаление сервера необратимо: пропадут все каналы и сообщения. Чтобы уйти, не удаляя сервер, передайте владение другому участнику в разделе «Участники».' : 'Вы можете покинуть сервер в любой момент. Вернуться получится по новому приглашению.'}
      </p>
      <div className={layer.buttonRow}>
        {owner ? (
          <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setConfirm('delete')}>Удалить сервер</button>
        ) : (
          <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setConfirm('leave')}>Покинуть сервер</button>
        )}
      </div>
      {confirm ? (
        <ConfirmModal
          title={confirm === 'delete' ? `Удалить «${guild.name}»` : `Покинуть «${guild.name}»`}
          text={confirm === 'delete' ? 'Сервер, каналы и сообщения будут удалены навсегда.' : 'Вернуться можно будет только по новому приглашению.'}
          confirmLabel={confirm === 'delete' ? 'Удалить сервер' : 'Покинуть сервер'}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            if (confirm === 'delete') await deleteGuild(guild.id)
            else await leaveGuild(guild.id)
            close()
            setView({ kind: 'home', page: 'friends' })
          }}
        />
      ) : null}
    </section>
  )
}

// --- Роли --------------------------------------------------------------------------

function Roles({ guild, self, perms }: { guild: Guild; self: Member; perms: bigint }) {
  const createRole = useGuildStore((s) => s.createRole)
  const updateRole = useGuildStore((s) => s.updateRole)
  const deleteRole = useGuildStore((s) => s.deleteRole)
  const reorderRoles = useGuildStore((s) => s.reorderRoles)
  const sorted = useMemo(() => [...guild.roles].sort((a, b) => b.position - a.position), [guild.roles])
  const [selectedId, setSelectedId] = useState<string>(sorted[0]?.id ?? guild.id)
  const role = guild.roles.find((r) => r.id === selectedId) ?? sorted[0]
  // на телефоне список и редактор рядом не помещаются: нажатие на роль открывает редактор вместо списка
  const [editing, setEditing] = useState(false)
  const open = (id: string) => {
    setSelectedId(id)
    setEditing(true)
  }
  // своя самая высокая роль и всё, что выше, закрыты замком — как у Discord
  const lockOf = (r: Role) => roleLock(guild, self, r)
  const editable = (r: Role) => lockOf(r) === null
  const [error, setError] = useState<string | null>(null)

  const create = async () => {
    try {
      const r = await createRole(guild.id, { name: 'новая роль' })
      open(r.id)
    } catch (err) {
      setError(describeError(err).message)
    }
  }
  const move = async (r: Role, delta: number) => {
    const pos = Math.max(1, r.position + delta)
    try {
      await reorderRoles(guild.id, [{ id: r.id, position: pos }])
    } catch (err) {
      setError(describeError(err).message)
    }
  }

  return (
    <section>
      <h1 className={layer.sectionTitle}>Роли</h1>
      <p className={layer.text}>Роли задают права и цвет имени. Выше в списке — важнее: редактировать можно только роли ниже своей высшей.</p>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.rolesLayout} data-editing={editing ? '' : undefined}>
        <div className={styles.rolesColumn}>
          <button type="button" className={cx(layer.button, styles.createRole)} onClick={() => void create()}><Plus size={16} /> Создать роль</button>
          <ul className={styles.roleList} aria-label="Роли">
            {sorted.map((r, i) => {
              const lock = lockOf(r)
              return (
                <li key={r.id} className={cx(styles.roleItem, role?.id === r.id && styles.roleItemActive)} data-tooltip={lock ? ROLE_LOCK_EDIT[lock] : undefined}>
                  <button type="button" className={styles.roleButton} onClick={() => open(r.id)}>
                    <span className={styles.roleDot} style={{ background: r.color ? hex(r.color) : 'var(--text-muted)' }} />
                    <span className={styles.roleName}>{r.name}</span>
                    {lock ? <Lock size={14} className={styles.roleLock} aria-label={ROLE_LOCK_EDIT[lock]} /> : null}
                  </button>
                  {r.id !== guild.id && !lock ? (
                    <span className={styles.roleMove}>
                      <button type="button" aria-label="Выше" disabled={i === 0 || !editable(sorted[i - 1]!)} onClick={() => void move(r, +1)}><ArrowUp size={14} /></button>
                      <button type="button" aria-label="Ниже" disabled={i >= sorted.length - 2} onClick={() => void move(r, -1)}><ArrowDown size={14} /></button>
                    </span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
        {role ? (
          <div className={styles.roleEditorWrap}>
            <button type="button" className={styles.rolesBack} onClick={() => setEditing(false)}><ChevronLeft size={18} /> Все роли</button>
            <RoleEditor key={role.id} guild={guild} role={role} perms={perms} lock={lockOf(role)} onSave={(patch) => updateRole(guild.id, role.id, patch)} onDelete={async () => { await deleteRole(guild.id, role.id); setSelectedId(guild.id); setEditing(false) }} />
          </div>
        ) : null}
      </div>
    </section>
  )
}

function RoleEditor({ guild, role, perms, lock, onSave, onDelete }: { guild: Guild; role: Role; perms: bigint; lock: RoleLock | null; onSave: (patch: Parameters<typeof guildApi.updateRole>[2]) => Promise<unknown>; onDelete: () => Promise<void> }) {
  const everyone = role.id === guild.id
  const editable = lock === null
  const [name, setName] = useState(role.name)
  const [color, setColor] = useState(role.color ? hex(role.color) : '#000000')
  const [hoist, setHoist] = useState(role.hoist)
  const [mentionable, setMentionable] = useState(role.mentionable)
  const [mask, setMask] = useState<bigint>(bits(role.permissions))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const canGrant = (key: PermissionKey) => perms === ALL || has(perms, P[key])
  const toggle = (key: PermissionKey) => setMask((m) => (has(m, P[key]) ? m & ~P[key] : m | P[key]))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const patch: Parameters<typeof guildApi.updateRole>[2] = { permissions: mask.toString() }
      if (!everyone) {
        patch.name = name.trim()
        patch.color = color === '#000000' ? 0 : parseInt(color.slice(1), 16)
        patch.hoist = hoist
        patch.mentionable = mentionable
      }
      await onSave(patch)
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.name ?? d.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.roleEditor}>
      {!everyone ? (
        <>
          <Field label="Название роли" htmlFor="role-name" error={error ?? undefined}>
            <ChannelNameInput id="role-name" value={name} onChange={setName} disabled={!editable} />
          </Field>
          <div className={styles.field}>
            <span className={auth.label}>Цвет роли</span>
            <div className={styles.swatches}>
              <input type="color" className={styles.colorInput} value={color} onChange={(e) => setColor(e.target.value)} disabled={!editable} aria-label="Свой цвет" />
              <button type="button" className={cx(styles.swatch, color === '#000000' && styles.swatchActive)} style={{ background: 'var(--text-muted)' }} onClick={() => setColor('#000000')} disabled={!editable} aria-label="Без цвета" />
              {SWATCHES.map((c) => <button key={c} type="button" className={cx(styles.swatch, color === c && styles.swatchActive)} style={{ background: c }} onClick={() => setColor(c)} disabled={!editable} aria-label={c} />)}
            </div>
          </div>
          <label className={styles.switch}>
            <input type="checkbox" checked={hoist} onChange={(e) => setHoist(e.target.checked)} disabled={!editable} />
            <span>Показывать участников с этой ролью отдельной группой</span>
          </label>
          <label className={styles.switch}>
            <input type="checkbox" checked={mentionable} onChange={(e) => setMentionable(e.target.checked)} disabled={!editable} />
            <span>Разрешить всем упоминать эту роль</span>
          </label>
        </>
      ) : (
        <p className={layer.text}>@everyone — базовые права каждого участника. Название и цвет у этой роли не меняются.</p>
      )}
      <h2 className={layer.subTitle}>Права</h2>
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.title} className={styles.permGroup}>
          <span className={styles.permGroupTitle}>{g.title}</span>
          {g.items.map((p) => (
            <label key={p.key} className={cx(styles.perm, (!editable || !canGrant(p.key)) && styles.permDisabled)}>
              <span className={styles.permText}>
                <span className={styles.permLabel}>{p.label}</span>
                {p.hint ? <span className={styles.permHint}>{p.hint}</span> : null}
              </span>
              <input type="checkbox" checked={has(mask, P[p.key])} onChange={() => toggle(p.key)} disabled={!editable || !canGrant(p.key)} />
            </label>
          ))}
        </div>
      ))}
      {error && everyone ? <p className={styles.error} role="alert">{error}</p> : null}
      {editable ? (
        <div className={cx(layer.buttonRow, styles.editorFooter)}>
          <button type="button" className={layer.button} disabled={busy || (!everyone && !name.trim())} onClick={() => void save()}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
          {!everyone ? <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setDeleting(true)}>Удалить роль</button> : null}
        </div>
      ) : lock ? (
        <p className={cx(layer.text, styles.lockNote)}><Lock size={14} aria-hidden /> {ROLE_LOCK_EDIT[lock]}.</p>
      ) : null}
      {deleting ? <ConfirmModal title={`Удалить роль «${role.name}»`} text="Роль будет снята со всех участников." confirmLabel="Удалить" onCancel={() => setDeleting(false)} onConfirm={onDelete} /> : null}
    </div>
  )
}

// --- Участники -----------------------------------------------------------------------

function Members({ guild, self, perms }: { guild: Guild; self: Member; perms: bigint }) {
  const members = useGuildStore((s) => s.members[guild.id])
  const loadMembers = useGuildStore((s) => s.loadMembers)
  const updateMember = useGuildStore((s) => s.updateMember)
  const setMemberRole = useGuildStore((s) => s.setMemberRole)
  const updateGuild = useGuildStore((s) => s.updateGuild)
  const kick = useGuildStore((s) => s.kick)
  const ban = useGuildStore((s) => s.ban)
  const presences = useSocialStore((s) => s.presences)
  const selfStatus = useSelfStatus()
  const [query, setQuery] = useState('')
  const [rolesFor, setRolesFor] = useState<string | null>(null)
  const closeRoles = useCallback(() => setRolesFor(null), [])
  const [confirm, setConfirm] = useState<{ kind: 'kick' | 'ban' | 'transfer'; m: Member } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void loadMembers(guild.id)
  }, [guild.id, loadMembers])

  const owner = guild.owner_id === self.user.id
  const myTop = owner ? Number.MAX_SAFE_INTEGER : (topRole(guild.roles, self.roles)?.position ?? 0)
  const outranks = (m: Member) => m.user.id !== self.user.id && m.user.id !== guild.owner_id && (owner || (topRole(guild.roles, m.roles)?.position ?? 0) < myTop)
  // у участника видно все роли, кроме @everyone: те, что не выдать, — под замком
  const roles = guild.roles.filter((r) => r.id !== guild.id).sort((a, b) => b.position - a.position)
  const list = useMemo(() => {
    const q = query.trim().toLowerCase()
    return Object.values(members ?? {})
      .filter((m) => !q || m.user.username.includes(q) || memberName(m).toLowerCase().includes(q))
      .sort((a, b) => memberName(a).localeCompare(memberName(b), 'ru'))
  }, [members, query])

  const run = (fn: () => Promise<unknown>) => async () => {
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(describeError(err).message)
    }
  }
  const toggleRole = (m: Member, roleId: string, on: boolean) => run(() => setMemberRole(guild.id, m.user.id, roleId, on))
  const timeout = (m: Member, seconds: number | null) => run(async () => {
    await updateMember(guild.id, m.user.id, { communication_disabled_until: seconds ? new Date(Date.now() + seconds * 1000).toISOString() : null })
  })

  return (
    <section>
      <h1 className={layer.sectionTitle}>Участники — {guild.member_count ?? list.length}</h1>
      <input className={cx(auth.input, styles.search)} placeholder="Поиск по имени или нику" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск участников" />
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <ul className={styles.memberList}>
        {list.map((m) => {
          const name = memberName(m)
          const color = roleColor(guild.roles, m.roles)
          const status = m.user.id === self.user.id ? selfStatus : (presences[m.user.id] ?? 'offline')
          const inTimeout = Boolean(m.communication_disabled_until && new Date(m.communication_disabled_until) > new Date())
          const canAct = outranks(m)
          return (
            <li key={m.user.id} className={styles.memberRow}>
              <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(m.user.id)} size={32} status={m.user.id === self.user.id || isOnline(status) ? status : 'offline'} src={memberAvatarUrl(guild.id, m)} decoration={m.user.avatar_decoration_data} />
              <div className={styles.memberText}>
                <span className={styles.memberName} style={color ? { color } : undefined}>{name} <span className={styles.memberUsername}>@{m.user.username}</span>{m.user.id === guild.owner_id ? <span className={styles.ownerTag}>владелец</span> : null}{inTimeout ? <span className={styles.timeoutTag}>таймаут</span> : null}</span>
                <span className={styles.memberRoles}>
                  {m.roles.map((rid) => guild.roles.find((r) => r.id === rid)).filter((r): r is Role => Boolean(r)).sort((a, b) => b.position - a.position).map((r) => (
                    <span key={r.id} className={styles.roleChip}><span className={styles.roleDot} style={{ background: r.color ? hex(r.color) : 'var(--text-muted)' }} />{r.name}</span>
                  ))}
                  {m.roles.length === 0 ? <span className={styles.noRoles}>без ролей</span> : null}
                </span>
              </div>
              <div className={styles.memberActions}>
                {has(perms, P.MANAGE_ROLES) && roles.length > 0 && (canAct || m.user.id === self.user.id) ? (
                  <RolePicker guild={guild} self={self} member={m} roles={roles} open={rolesFor === m.user.id} onOpen={() => setRolesFor(m.user.id)} onClose={closeRoles} onToggle={(roleId, on) => void toggleRole(m, roleId, on)()} />
                ) : null}
                {has(perms, P.MODERATE_MEMBERS) && canAct ? (
                  inTimeout ? <button type="button" className={styles.smallButton} onClick={() => void timeout(m, null)()}>Снять таймаут</button> : <button type="button" className={styles.smallButton} onClick={() => void timeout(m, 3600)()}>Таймаут 1 ч</button>
                ) : null}
                {has(perms, P.KICK_MEMBERS) && canAct ? <button type="button" className={cx(styles.smallButton, styles.smallDanger)} onClick={() => setConfirm({ kind: 'kick', m })}>Выгнать</button> : null}
                {has(perms, P.BAN_MEMBERS) && canAct ? <button type="button" className={cx(styles.smallButton, styles.smallDanger)} onClick={() => setConfirm({ kind: 'ban', m })}>Забанить</button> : null}
                {owner && m.user.id !== self.user.id ? <button type="button" className={styles.smallButton} onClick={() => setConfirm({ kind: 'transfer', m })}>Передать сервер</button> : null}
              </div>
            </li>
          )
        })}
      </ul>
      {confirm ? (
        <ConfirmModal
          title={confirm.kind === 'kick' ? `Выгнать ${memberName(confirm.m)}` : confirm.kind === 'ban' ? `Забанить ${memberName(confirm.m)}` : `Передать сервер ${memberName(confirm.m)}`}
          text={confirm.kind === 'kick' ? 'Участник сможет вернуться по новому приглашению.' : confirm.kind === 'ban' ? 'Участник не сможет вернуться, пока бан не снят.' : 'Вы перестанете быть владельцем сервера. Отменить это сможет только новый владелец.'}
          confirmLabel={confirm.kind === 'kick' ? 'Выгнать' : confirm.kind === 'ban' ? 'Забанить' : 'Передать'}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            if (confirm.kind === 'kick') await kick(guild.id, confirm.m.user.id)
            else if (confirm.kind === 'ban') await ban(guild.id, confirm.m.user.id)
            else await updateGuild(guild.id, { owner_id: confirm.m.user.id })
            setConfirm(null)
          }}
        />
      ) : null}
    </section>
  )
}

/**
 * Роли участника: кнопка и список с галочками. Закрывается щелчком мимо и Esc, как остальные
 * всплывающие меню. Роли, которые не выдать и не снять, видно под замком с подсказкой.
 */
function RolePicker({ guild, self, member, roles, open, onOpen, onClose, onToggle }: { guild: Guild; self: Member; member: Member; roles: Role[]; open: boolean; onOpen: () => void; onClose: () => void; onToggle: (roleId: string, on: boolean) => void }) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    // кнопка «Роли» — внутри ref: щелчок по ней не «мимо», она сама закрывает список
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    // Esc закрывает только список: настройки сервера под ним остаются открытыми
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open, onClose])

  return (
    <div ref={ref} className={styles.rolePicker}>
      <button type="button" className={styles.smallButton} onClick={open ? onClose : onOpen} aria-expanded={open}>Роли</button>
      {open ? (
        <div className={cx(styles.roleMenu, 'klichat-popover')} role="group" aria-label={`Роли: ${memberName(member)}`} style={{ '--popover-origin': 'top right' } as CSSProperties}>
          {roles.map((r) => {
            const hint = assignLockHint(guild, self, r)
            return (
              <label key={r.id} className={cx(styles.roleOption, hint && styles.roleOptionLocked)} data-tooltip={hint}>
                <input type="checkbox" checked={member.roles.includes(r.id)} disabled={Boolean(hint)} onChange={(e) => onToggle(r.id, e.target.checked)} />
                <span className={styles.roleDot} style={{ background: r.color ? hex(r.color) : 'var(--text-muted)' }} />
                <span className={styles.roleName}>{r.name}</span>
                {hint ? <Lock size={12} className={styles.roleLock} aria-label={hint} /> : null}
              </label>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

// --- Приглашения ---------------------------------------------------------------------

function Invites({ guild }: { guild: Guild }) {
  const qc = useQueryClient()
  const invites = useQuery({ queryKey: ['invites', guild.id], queryFn: () => guildApi.invites(guild.id) })
  const revoke = useMutation({ mutationFn: (code: string) => guildApi.deleteInvite(code), onSuccess: () => void qc.invalidateQueries({ queryKey: ['invites', guild.id] }) })
  const list = invites.data ?? []
  return (
    <section>
      <h1 className={layer.sectionTitle}>Приглашения</h1>
      <p className={layer.text}>Действующие ссылки на сервер. Создать новую можно из меню сервера — «Пригласить людей».</p>
      {invites.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {list.length === 0 && !invites.isPending ? <p className={layer.text}>Приглашений нет.</p> : null}
      <ul className={styles.table}>
        {list.map((inv) => (
          <li key={inv.code} className={styles.tableRow}>
            <div className={styles.tableMain}>
              <code className={styles.code}>{inviteLink(inv.code)}</code>
              <span className={styles.tableMeta}>
                {inv.inviter ? `${displayName(inv.inviter)} · ` : ''}#{inv.channel.name} · использовано {inv.uses}{inv.max_uses ? ` из ${inv.max_uses}` : ''} ·{' '}
                {inv.expires_at ? `до ${formatDateTime(new Date(inv.expires_at))}` : 'бессрочно'}
              </span>
            </div>
            <button type="button" className={cx(styles.smallButton, styles.smallDanger)} disabled={revoke.isPending} onClick={() => revoke.mutate(inv.code)}>Отозвать</button>
          </li>
        ))}
      </ul>
    </section>
  )
}

// --- Баны ---------------------------------------------------------------------------

function Bans({ guild }: { guild: Guild }) {
  const qc = useQueryClient()
  const bans = useQuery({ queryKey: ['bans', guild.id], queryFn: () => guildApi.bans(guild.id) })
  const unban = useMutation({ mutationFn: (userId: string) => guildApi.unban(guild.id, userId), onSuccess: () => void qc.invalidateQueries({ queryKey: ['bans', guild.id] }) })
  const list = bans.data ?? []
  return (
    <section>
      <h1 className={layer.sectionTitle}>Баны — {list.length}</h1>
      {bans.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {list.length === 0 && !bans.isPending ? <p className={layer.text}>Забаненных нет.</p> : null}
      <ul className={styles.table}>
        {list.map((b) => (
          <li key={b.user.id} className={styles.tableRow}>
            <Avatar initial={displayName(b.user).slice(0, 1).toUpperCase()} color={colorOf(b.user.id)} size={32} src={avatarUrl(b.user.id, b.user.avatar)} decoration={b.user.avatar_decoration_data} />
            <div className={styles.tableMain}>
              <span className={styles.memberName}>{displayName(b.user)} <span className={styles.memberUsername}>@{b.user.username}</span></span>
              <span className={styles.tableMeta}>{b.reason ? `Причина: ${b.reason}` : 'Без причины'}</span>
            </div>
            <button type="button" className={styles.smallButton} disabled={unban.isPending} onClick={() => unban.mutate(b.user.id)}>Разбанить</button>
          </li>
        ))}
      </ul>
      <p className={layer.text}>{plural(list.length, 'Бан', 'Бана', 'Банов')} на сервере: {list.length}.</p>
    </section>
  )
}

// --- Журнал аудита ----------------------------------------------------------------------

const ACTION_LABELS: Record<number, string> = {
  1: 'изменил(а) сервер',
  10: 'создал(а) канал',
  11: 'изменил(а) канал',
  12: 'удалил(а) канал',
  13: 'добавил(а) перекрытие прав',
  14: 'изменил(а) перекрытие прав',
  15: 'удалил(а) перекрытие прав',
  20: 'выгнал(а)',
  22: 'забанил(а)',
  23: 'разбанил(а)',
  24: 'изменил(а) участника',
  25: 'изменил(а) роли участника',
  28: 'добавил(а) бота',
  30: 'создал(а) роль',
  31: 'изменил(а) роль',
  32: 'удалил(а) роль',
  40: 'создал(а) приглашение',
  42: 'отозвал(а) приглашение',
  50: 'создал(а) вебхук',
  51: 'изменил(а) вебхук',
  52: 'удалил(а) вебхук',
  60: 'добавил(а) эмодзи',
  61: 'переименовал(а) эмодзи',
  62: 'удалил(а) эмодзи',
  72: 'удалил(а) сообщение участника',
  73: 'удалил(а)',
  74: 'закрепил(а) сообщение',
  75: 'открепил(а) сообщение',
  90: 'добавил(а) стикер',
  91: 'изменил(а) стикер',
  92: 'удалил(а) стикер',
  190: 'импортировал(а) структуру из Discord',
  110: 'создал(а) ветку',
  111: 'изменил(а) ветку',
  112: 'удалил(а) ветку',
  130: 'добавил(а) звук',
  131: 'изменил(а) звук',
  132: 'удалил(а) звук',
  140: 'создал(а) правило автомода',
  141: 'изменил(а) правило автомода',
  142: 'удалил(а) правило автомода',
  143: 'заблокировал(а) сообщение',
  144: 'оповестил(а) модераторов',
  145: 'выдал(а) таймаут',
}

const ACTION_FILTERS: { value: number; label: string }[] = [
  { value: 0, label: 'Все действия' },
  { value: 1, label: 'Сервер' },
  { value: 10, label: 'Создание канала' },
  { value: 11, label: 'Изменение канала' },
  { value: 12, label: 'Удаление канала' },
  { value: 20, label: 'Кик' },
  { value: 22, label: 'Бан' },
  { value: 23, label: 'Разбан' },
  { value: 24, label: 'Изменение участника' },
  { value: 25, label: 'Роли участника' },
  { value: 30, label: 'Создание роли' },
  { value: 31, label: 'Изменение роли' },
  { value: 32, label: 'Удаление роли' },
  { value: 40, label: 'Приглашения' },
  { value: 28, label: 'Добавление ботов' },
  { value: 50, label: 'Вебхуки' },
  { value: 72, label: 'Удаление сообщений' },
  { value: 73, label: 'Массовое удаление сообщений' },
  { value: 143, label: 'Автомод: блокировки' },
  { value: 145, label: 'Автомод: таймауты' },
]

function AuditLogSection({ guild }: { guild: Guild }) {
  const channels = useGuildStore((s) => s.channels[guild.id])
  const [action, setAction] = useState(0)
  const key = `${guild.id}|${action}`
  // страница журнала привязана к фильтру: смена фильтра начинает новую загрузку
  const [page, setPage] = useState<{ key: string; entries: AuditLogEntry[]; users: Record<string, User>; done: boolean; error: string | null } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const current = page?.key === key ? page : null
  const entries = current?.entries ?? []
  const users = current?.users ?? {}
  const done = current?.done ?? false
  const error = current?.error ?? null
  const loading = !current || loadingMore

  useEffect(() => {
    let cancelled = false
    guildApi
      .auditLog(guild.id, { ...(action ? { action_type: action } : {}), limit: 50 })
      .then((res) => {
        if (cancelled) return
        setPage({ key, entries: res.audit_log_entries, users: Object.fromEntries(res.users.map((u) => [u.id, u])), done: res.audit_log_entries.length < 50, error: null })
      })
      .catch((err: unknown) => {
        if (!cancelled) setPage({ key, entries: [], users: {}, done: true, error: describeError(err).message })
      })
    return () => {
      cancelled = true
    }
  }, [guild.id, action, key])

  const loadMore = async () => {
    const last = entries[entries.length - 1]
    if (!last) return
    setLoadingMore(true)
    try {
      const res = await guildApi.auditLog(guild.id, { ...(action ? { action_type: action } : {}), before: last.id, limit: 50 })
      setPage((p) => (p && p.key === key ? { ...p, entries: [...p.entries, ...res.audit_log_entries], users: { ...p.users, ...Object.fromEntries(res.users.map((u) => [u.id, u])) }, done: res.audit_log_entries.length < 50 } : p))
    } catch (err) {
      setPage((p) => (p && p.key === key ? { ...p, error: describeError(err).message } : p))
    } finally {
      setLoadingMore(false)
    }
  }

  const who = (id: string | null) => (id && users[id] ? displayName(users[id]!) : id ? 'кто-то' : 'Автомод')
  // своё сообщение: автор и цель совпадают, имя второй раз не повторяем
  const ownDelete = (e: AuditLogEntry) => e.action_type === 72 && e.user_id !== null && e.user_id === e.target_id
  const actionLabel = (e: AuditLogEntry) => (ownDelete(e) ? 'удалил(а) своё сообщение' : ACTION_LABELS[e.action_type] ?? `действие ${e.action_type}`)
  const target = (e: AuditLogEntry) => {
    if (e.action_type === 73) {
      const id = e.options['channel_id']
      const ch = typeof id === 'string' ? channels?.[id] : undefined
      return `${e.options['count']} ${plural(Number(e.options['count']), 'сообщение', 'сообщения', 'сообщений')}${ch ? ` в «${ch.name}»` : ''}`
    }
    if (!e.target_id) return ''
    if (e.action_type === 72) {
      const id = e.options['channel_id']
      const ch = typeof id === 'string' ? channels?.[id] : undefined
      return [ownDelete(e) ? '' : who(e.target_id), ch ? `в #${ch.name}` : ''].filter(Boolean).join(' ')
    }
    if ([20, 22, 23, 24, 25, 143, 144, 145].includes(e.action_type)) return who(e.target_id)
    if ([10, 11, 12, 13, 14, 15].includes(e.action_type)) {
      const ch = channels?.[e.target_id]
      if (ch) return `#${ch.name}`
      const name = e.changes.find((c) => c.key === 'name')
      return name ? `#${String(name.new_value ?? name.old_value ?? '')}` : 'канал'
    }
    if ([30, 31, 32].includes(e.action_type)) {
      const role = guild.roles.find((r) => r.id === e.target_id)
      const name = e.changes.find((c) => c.key === 'name')
      return role ? role.name : name ? String(name.new_value ?? name.old_value ?? '') : 'роль'
    }
    return ''
  }
  const details = (e: AuditLogEntry) =>
    e.changes
      .filter((c) => c.key !== 'permission_overwrites' && c.key !== '$roles' && c.key !== '$add' && c.key !== '$remove')
      .map((c) => `${c.key}: ${fmt(c.old_value)} → ${fmt(c.new_value)}`)
      .join(' · ')

  return (
    <section>
      <h1 className={layer.sectionTitle}>Журнал аудита</h1>
      <p className={layer.text}>Кто и что менял на сервере за последние 90 дней. Причину действия модераторы указывают при кике, бане и удалении сообщений.</p>
      <Select className={styles.search} value={action} options={ACTION_FILTERS} onChange={setAction} label="Фильтр по действию" />
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <ul className={styles.table}>
        {entries.map((e) => (
          <li key={e.id} className={styles.tableRow}>
            <Avatar initial={who(e.user_id).slice(0, 1).toUpperCase()} color={colorOf(e.user_id ?? '0')} size={32} src={e.user_id ? avatarUrl(e.user_id, users[e.user_id]?.avatar) : undefined} decoration={e.user_id ? users[e.user_id]?.avatar_decoration_data : undefined} />
            <div className={styles.tableMain}>
              <span className={styles.auditLine}>
                {who(e.user_id)} <span className={styles.auditAction}>{actionLabel(e)}</span> {target(e)}
              </span>
              <span className={styles.tableMeta}>
                {formatDateTime(new Date(e.created_at))}
                {e.reason ? ` · причина: ${e.reason}` : ''}
                {details(e) ? ` · ${details(e)}` : ''}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {entries.length === 0 && !loading ? <p className={layer.text}>Записей пока нет.</p> : null}
      {!done && entries.length > 0 ? (
        <div className={layer.buttonRow}>
          <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={loading} onClick={() => void loadMore()}>{loading ? 'Загружаем…' : 'Показать ещё'}</button>
        </div>
      ) : null}
    </section>
  )
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v || '—'
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}
